import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/free-solid-svg-icons';

import type { IWalletCashOutBrand, WalletCashOutStatus } from '@share/common-types';

import styles from './styles.module.scss';

export type StatusTone = 'processing' | 'review' | 'delivered' | 'failed';

// Same buckets as the cash-out page's status badge (cash-out-status/component.tsx).
const STATUS_TONE: Record<WalletCashOutStatus, StatusTone> = {
  Requested: 'processing',
  Ordered: 'processing',
  FlaggedForReview: 'processing',
  NeedsReview: 'review',
  Delivered: 'delivered',
  DeliveryFailed: 'delivered',
  Failed: 'failed',
  Canceled: 'failed',
  Reversed: 'failed',
};

const STATUS_LABEL: Record<StatusTone, string> = {
  processing: 'wallet.cashout.status.processing',
  review: 'wallet.cashout.status.review',
  delivered: 'wallet.cashout.status.delivered',
  failed: 'wallet.cashout.status.failed',
};

// Catalog logos are third-party URLs that can fail to load: fall back to the gift icon.
export const BrandLogo = ({ brand }: { brand?: IWalletCashOutBrand }): React.ReactElement => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const url = brand?.imageUrl && brand.imageUrl !== failedUrl ? brand.imageUrl : null;
  return (
    <span className={styles.brandLogo} aria-hidden="true">
      {url ? <img src={url} alt="" onError={() => setFailedUrl(url)} /> : <FontAwesomeIcon icon={faGift} />}
    </span>
  );
};

export const statusTone = (status: WalletCashOutStatus): StatusTone => STATUS_TONE[status] ?? 'processing';

export const StatusBadge = ({ status }: { status: WalletCashOutStatus }): React.ReactElement => {
  const intl = useIntl();
  const tone = statusTone(status);
  return (
    <span className={`${styles.status} ${styles[`status_${tone}`]}`}>
      {intl.formatMessage({ id: STATUS_LABEL[tone] })}
    </span>
  );
};
